package com.siriusxm.transcripts

/**
 * Represents a single cue (subtitle entry) in a WebVTT document.
 *
 * @property startTime The start time in milliseconds
 * @property endTime The end time in milliseconds
 * @property text The transcript text for this cue
 * @property index The cue number (0-based)
 * @property speaker The speaker name (if annotated with <v Speaker Name>), null otherwise
 */
data class VTTCue(
    val startTime: Long,
    val endTime: Long,
    val text: String,
    val index: Int,
    val speaker: String? = null
)

/**
 * Represents a parsed WebVTT document.
 *
 * @property cues List of all cues in the document
 * @property raw The original VTT text
 */
data class VTTDocument(
    val cues: List<VTTCue>,
    val raw: String
)

/**
 * Formats a time in milliseconds to a human-readable timestamp.
 * Returns MM:SS for times under 1 hour, HH:MM:SS otherwise.
 *
 * @param milliseconds The time in milliseconds
 * @return Formatted timestamp string
 */
fun formatTimestamp(milliseconds: Long): String {
    val totalSeconds = (milliseconds / 1000).toInt()
    val hours = totalSeconds / 3600
    val minutes = (totalSeconds % 3600) / 60
    val secs = totalSeconds % 60

    val hoursStr = hours.toString().padStart(2, '0')
    val minutesStr = minutes.toString().padStart(2, '0')
    val secsStr = secs.toString().padStart(2, '0')

    return if (hours > 0) {
        "$hoursStr:$minutesStr:$secsStr"
    } else {
        "$minutesStr:$secsStr"
    }
}
